import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { LoadingComponent, MultiCheckDropdown } from "Components";
import { Modal } from "react-bootstrap";
import { Validator } from "Utils/validator";

const LOGIC_OPTIONS = [
  { label: "AND", value: "and" },
  { label: "OR", value: "or" },
];

export const AddConditionalBlock = ({
  show,
  toggle,
  variableLoading,
  variableOptions,
  condBlockDataLoading,
  condBlockData,
  resetCondBlockData,
  fetchOperatorsAndValue,
  editor,
  editState,
  onSave,
}) => {
  const [logic, setLogic] = useState("");
  const [conditions, setConditions] = useState([]);
  const [errMsg, setErrMsg] = useState({
    logic: "",
    conditions: "",
  });

  useEffect(() => {
    if (editState) {
      setLogic(editState.logic);
      // Ensure each condition has an ID
      const conditionsWithIds = editState.conditions.map((cond) => ({
        ...cond,
        id: cond.id || uuidv4(),
      }));

      for (const condition of conditionsWithIds) {
        const { id, variable } = condition;
        fetchOperatorsAndValue(variable, id);
      }

      setConditions(conditionsWithIds);
    }
  }, [editState]);

  const handleChangeLogic = (e) => {
    const { value } = e.target;
    setLogic(value);
    if (!Validator.text(value) && conditions.length > 1) {
      setErrMsg((prev) => ({
        ...prev,
        logic: "Please select logic.",
      }));
    } else {
      setErrMsg((prev) => ({
        ...prev,
        logic: "",
      }));
    }
  };

  const addCondition = () => {
    setConditions([
      ...conditions,
      { id: uuidv4(), variable: "", operator: "", value: "" },
    ]);
    setErrMsg((prev) => ({
      ...prev,
      conditions: "",
    }));
  };

  const removeCondition = (id) => {
    setConditions(conditions.filter((cond) => cond.id !== id));
  };

  const handleChangeCondition = (id, key, val) => {
    // Update the specific condition
    const newConds = conditions.map((cond) =>
      cond.id === id ? { ...cond, [key]: val } : cond
    );
    setConditions(newConds);

    // Build dynamic errors object
    const newErrors = {};
    newConds.forEach((cond) => {
      const condErrors = {};
      if (!Validator.text(cond.variable)) {
        condErrors.variable = "Please select variable.";
      } else {
        condErrors.variable = "";
      }

      if (!Validator.text(cond.operator)) {
        condErrors.operator = "Please select operator.";
      } else {
        condErrors.operator = "";
      }

      if (!Validator.text(cond.value)) {
        condErrors.value = "Please enter value.";
      } else {
        condErrors.value = "";
      }

      // Only add this condition if it has errors
      if (Object.keys(condErrors).length > 0) {
        newErrors[cond.id] = condErrors;
      }
    });
    // Update state once
    setErrMsg((prev) => ({
      ...prev,
      ...newErrors,
    }));

    // Fetch operators/values if variable changed
    if (key === "variable") {
      fetchOperatorsAndValue(val, id);
    }
  };

  const getValidation = async () => {
    return new Promise((resolve) => {
      let errorMessages = {};
      let isValid = true;

      if (conditions.length === 0) {
        isValid = false;
        errorMessages.conditions = "Please add at least one condition.";
      }

      if (!logic && conditions.length > 1) {
        isValid = false;
        errorMessages.logic = "Please select logic.";
      }

      conditions.forEach((cond) => {
        // Use logical OR to initialize nested object if it doesn't exist
        const condErrors = (errorMessages[cond.id] =
          errorMessages[cond.id] || {});

        if (!Validator.text(cond.variable)) {
          isValid = false;
          condErrors.variable = "Please select variable.";
        }
        if (!Validator.text(cond.operator)) {
          isValid = false;
          condErrors.operator = "Please select operator.";
        }
        if (!Validator.text(cond.value)) {
          isValid = false;
          condErrors.value = "Please enter value.";
        }
      });

      setErrMsg(errorMessages);
      resolve(isValid);
    });
  };

  const handleUpdateSelectedBlock = (editor, pos, logic, conditions) => {
    if (!editor || typeof pos !== "number") return;

    editor.chain().focus().updateConditionalBlock(pos, logic, conditions).run();
  };

  const onSaveConditionalBlock = async () => {
    if (!editor) return;

    const isValid = await getValidation();
    if (!isValid) return;

    if (editState) {
      handleUpdateSelectedBlock(editor, editState.pos, logic, conditions);
      onSave?.();
    } else {
      editor.commands.insertConditionalBlock(logic, conditions);
    }

    onClose();
  };

  const onClose = () => {
    setConditions([]);
    setLogic("");
    resetCondBlockData?.();
    toggle();
  };

  // Render component
  const renderValueField = (fieldDetils, fieldValue, condId) => {
    if (fieldDetils.designType === "select") {
      const options =
        fieldDetils.dropdownValue &&
        fieldDetils.dropdownValue.length > 0 &&
        fieldDetils.dropdownValue.map((option) => ({
          value: option.value,
          label: option.name || "",
        }));
      const selectedValues =
        options && fieldValue
          ? options.filter((obj) => fieldValue.split(",").includes(obj.value))
          : [];
      return (
        <MultiCheckDropdown
          options={options}
          value={selectedValues}
          onChange={(values) =>
            handleChangeCondition(
              condId,
              "value",
              values.map((v) => v.value).join(",")
            )
          }
        />
      );
    }
    return (
      <input
        type="text"
        className="form-control"
        name="value"
        value={fieldValue}
        onChange={(e) => handleChangeCondition(condId, "value", e.target.value)}
      />
    );
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      className=" modal-slider fade right h-100"
    >
      <Modal.Header className="justify-content-center" closeButton>
        <Modal.Title>
          {editState ? "Edit" : "Add"} Conditional Block
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <LoadingComponent isLoading={variableLoading || condBlockDataLoading}>
          <div className="p-4">
            <p>To begin, add a condition by clicking the button below.</p>
            <div className="row gy-3">
              {conditions.length > 1 && (
                <div className="col-12">
                  <div className="d-flex align-items-center gap-3">
                    <label className="form-label mb-0">Logic:</label>

                    {/* Logic dropdown */}
                    <select
                      value={logic}
                      onChange={handleChangeLogic}
                      className="form-select"
                    >
                      <option value="">Select Logic</option>
                      {LOGIC_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errMsg.logic && <span className="error">{errMsg.logic}</span>}
                </div>
              )}
              <div className="col-12">
                <div className="row gy-3">
                  {/* Conditions list */}
                  {conditions.map((c) => (
                    <div key={c.id} className="col-12">
                      <div className="row gx-2 gy-3 offer-letter-form">
                        {/* Variable */}
                        <div className="col-6">
                          <select
                            value={c.variable}
                            onChange={(e) =>
                              handleChangeCondition(
                                c.id,
                                "variable",
                                e.target.value
                              )
                            }
                            className="form-select"
                          >
                            <option value="">Select Variable</option>
                            {variableOptions.map((opt, idx) => (
                              <option
                                key={`${opt.value}-${idx}`}
                                value={opt.value}
                              >
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {errMsg[c.id] && errMsg[c.id].variable && (
                            <span className="error">{errMsg[c.id].variable}</span>
                          )}
                        </div>

                        {/* Operator */}
                        <div className="col-6">
                          <select
                            value={c.operator}
                            onChange={(e) =>
                              handleChangeCondition(
                                c.id,
                                "operator",
                                e.target.value
                              )
                            }
                            className="form-select text-primary-800 bg-primary-50"
                          >
                            <option value="">Select Operator</option>
                            {condBlockData &&
                              condBlockData[c.id] &&
                              condBlockData[c.id].operators &&
                              condBlockData[c.id].operators.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                          </select>
                          {errMsg[c.id] && errMsg[c.id].operator && (
                            <span className="error">{errMsg[c.id].operator}</span>
                          )}
                        </div>

                        <div className="col-12">
                          <div className="row align-items-start">
                            {/* Value */}
                            <div className="col-11">
                              <div className="h-9">
                                {condBlockData &&
                                condBlockData[c.id] &&
                                condBlockData[c.id].values ? (
                                  renderValueField(
                                    condBlockData[c.id].values,
                                    c.value,
                                    c.id
                                  )
                                ) : (
                                  <input
                                    type="text"
                                    className="form-control"
                                    disabled
                                  />
                                )}
                                
                              </div>
                                {errMsg[c.id] && errMsg[c.id].value && (
                                  <span className="error">{errMsg[c.id].value}</span>
                                )}
                            </div>

                            {/* Remove */}
                            <div className="col-1 d-flex justify-content-end align-items-center">
                              <button
                                onClick={() => removeCondition(c.id)}
                                className="mt-1 flex-none size-6 btn btn-dark p-0 d-flex justify-content-center align-items-center rounded-circle"
                              >
                                <i className="bx bx-x text-base"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="col-12">
                    <button onClick={addCondition} className="btn btn-primary">
                      + Add Condition
                    </button>
                    {errMsg.conditions && (
                      <p className="error mt-1">{errMsg.conditions}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LoadingComponent>
      </Modal.Body>

      <Modal.Footer className="">
        <button
          onClick={onClose}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          onClick={onSaveConditionalBlock}
          className="btn btn-primary"
          disabled={variableLoading || condBlockDataLoading}
        >
          Save
        </button>
      </Modal.Footer>
    </Modal>
  );
};
